import {
	calculateDailyAllowance,
	type DayClose,
	dateAnchor,
	formatKstDate,
	formatWon,
	type HouseholdConsumptionState,
	kstDateKey,
	resolveLivingBudgetPlan,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useId, useState } from "react";
import { navigate } from "./chrome";
import { DayStamp } from "./DayClose";
import { monthStartDayPref } from "./prefs";
import { Sheet } from "./Sheet";

export function AllowanceEditor({
	household,
	onChange,
	onClose,
	onSet,
}: {
	household: HouseholdConsumptionState;
	onChange: (next: HouseholdConsumptionState) => Promise<boolean>;
	onClose: () => void;
	onSet: (source: string) => void;
}) {
	const plan = resolveLivingBudgetPlan(
		household,
		new Date(),
		monthStartDayPref(),
	);
	const proposed = Object.values(plan.limits).reduce((a, b) => a + b, 0);
	const [value, setValue] = useState(String(plan.monthlySpendable ?? proposed));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const amount = Number(value);
	const valid = Number.isSafeInteger(amount) && amount > 0;
	return (
		<Sheet
			title="이번 달 쓸 돈"
			onClose={onClose}
			footer={
				<button
					className="button full-width"
					type="button"
					disabled={busy || !valid}
					onClick={async () => {
						setBusy(true);
						const updated = { ...plan, monthlySpendable: amount };
						const ok = await onChange({
							...household,
							budgetPlans: [
								...household.budgetPlans.filter(
									(p) => p.period !== plan.period,
								),
								updated,
							],
						}).catch(() => false);
						setBusy(false);
						if (ok) {
							onSet(amount === proposed ? "limits_sum" : "manual");
							onClose();
						} else
							setError(
								"저장하지 못했어요. 적은 금액은 그대로예요. 다시 눌러 주세요.",
							);
					}}
				>
					{busy ? "저장 중…" : "쓸 돈 저장"}
				</button>
			}
		>
			<p>수입이나 계좌 잔액 대신, 이번 기간에 쓰려는 돈 한 칸만 적어요.</p>
			<label>
				이번 달 쓸 돈
				<input
					inputMode="numeric"
					value={value}
					onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
				/>
			</label>
			<p className="field-hint">
				기존 식비·생활용품·반복비 한도 합계 {formatWon(proposed)}를 제안했어요.
				한도는 그대로 유지돼요.
			</p>
			{!valid ? <p>1원 이상으로 적어 주세요.</p> : null}
			{error ? <p role="alert">{error}</p> : null}
		</Sheet>
	);
}
export function TodayScreen({
	receipts,
	household,
	closes,
	onRecord,
	onCloseDay,
	onAllowance,
	busy,
	error,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	closes: DayClose[];
	onRecord: () => void;
	onCloseDay: (date: string, quick?: boolean) => void;
	onAllowance: () => void;
	busy: boolean;
	error: string;
}) {
	const moneyTitle = useId();
	const now = new Date();
	const plan = resolveLivingBudgetPlan(household, now, monthStartDayPref());
	const today = calculateDailyAllowance(
		receipts,
		plan,
		household.recurringPayments,
		now,
	);
	const active = receipts.filter((r) => !r.deletedAt);
	const first = active.length === 0;
	const start = Date.parse(`${today.today}T00:00:00+09:00`);
	const weekday = new Date(start + 9 * 3600000).getUTCDay();
	const weekStart = start - ((weekday + 6) % 7) * 86400000;
	const pending = household.shopping.filter((e) => e.status === "pending");
	return (
		<>
			<section className="today-receipt" aria-labelledby={moneyTitle}>
				<div className="receipt-top">
					<span>{formatKstDate(now)}</span>
					<button className="quiet-button" type="button" onClick={onAllowance}>
						쓸 돈 {today.spendable === null ? "설정" : "바꾸기"}
					</button>
				</div>
				<h1 id={moneyTitle}>오늘 남은 돈</h1>
				<div className="today-money">
					{today.remaining === null
						? "한 건부터 남겨요"
						: formatWon(today.remaining)}
				</div>
				<p className="today-calculation">
					{today.allowance === null
						? "쓸 돈은 첫 기록 뒤에 정해도 돼요."
						: `하루 몫 ${formatWon(today.allowance)} 중 ${formatWon(today.spent)} 썼어요`}
				</p>
				{today.exceeded > 0 ? (
					<p>이번 달 쓸 돈보다 {formatWon(today.exceeded)} 더 썼어요.</p>
				) : today.overToday > 0 ? (
					<p>
						하루 몫보다 {formatWon(today.overToday)} 더 썼어요. 남은 날에 다시
						나눠요.
					</p>
				) : null}
				<div className="receipt-rule" />
				<h2>오늘 결제</h2>
				{today.todayRecords.length ? (
					<ul className="today-payments">
						{today.todayRecords.map((r, i) => (
							<li key={`${r.transactedAt}:${i}`}>
								<span>
									{r.storeDisplay}
									{r.recurringPaymentId ? (
										<small>정기 결제 · 하루 몫에서 다시 빼지 않아요</small>
									) : null}
								</span>
								<b>{formatWon(r.totalAmount)}</b>
							</li>
						))}
					</ul>
				) : (
					<p className="field-hint">
						오늘 기록은 아직 없어요. 결제 알림을 붙이면 여기에 남아요.
					</p>
				)}
				{today.pendingReserved > 0 ? (
					<div className="reserved-money">
						<span>정기 결제에 미리 빼 둔 돈</span>
						<b>{formatWon(today.pendingReserved)}</b>
					</div>
				) : null}
			</section>
			<section className="today-stamps" aria-label="이번 주 마감">
				<div className="section-heading">
					<h2>이번 주</h2>
					<button
						className="quiet-button"
						type="button"
						onClick={() => onCloseDay(today.today, true)}
					>
						오늘 마감
					</button>
				</div>
				<div className="week-stamps">
					{Array.from({ length: 7 }, (_, i) => {
						const key = kstDateKey(new Date(weekStart + i * 86400000));
						const close = closes.find((c) => c.date === key);
						return (
							<button
								type="button"
								key={key}
								disabled={key > today.today}
								aria-label={`${formatKstDate(dateAnchor(key))}${close ? " 마감한 날" : " 마감하기"}`}
								onClick={() => onCloseDay(key)}
							>
								<span>{["월", "화", "수", "목", "금", "토", "일"][i]}</span>
								{close ? (
									<DayStamp kind={close.kind} small />
								) : (
									<span className="empty-stamp">{Number(key.slice(-2))}</span>
								)}
							</button>
						);
					})}
				</div>
			</section>
			<button
				className="today-household"
				type="button"
				onClick={() => navigate("/app/household")}
			>
				<span>살림</span>
				<b>
					{pending.length
						? `살 것 ${pending.length}개`
						: "다 쓴 물건을 표시해요"}
				</b>
				<span aria-hidden="true">→</span>
			</button>
			{first ? (
				<p className="today-trust">
					서버에 보낸 알림 0건 · 연결한 카드 없음 · 가입 안 함
				</p>
			) : null}
			{error ? (
				<p role="alert" className="form-note">
					{error}
				</p>
			) : null}
			<div className="today-primary">
				<button
					className="button full-width"
					type="button"
					onClick={onRecord}
					disabled={busy}
				>
					{busy ? "알림 읽는 중…" : "결제 알림 붙여 넣기"}
				</button>
				<button
					className="quiet-button"
					type="button"
					onClick={() => navigate("/app/capture?mode=manual")}
				>
					직접 입력
				</button>
			</div>
		</>
	);
}
